import React, { Suspense, useContext, useEffect, useState } from 'react';
import { RouteComponentProps, useLocation } from '@reach/router';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import MenuIcon from '@mui/icons-material/Menu';
import Paper from '@mui/material/Paper';
import { useIsFetching } from '@tanstack/react-query';

import { LoadingCircle } from '../../atoms';
import { SideNav } from '../../organisms';

import StyledAdmin from './admin.style';
import { ApplicationContext } from '@particles';
import { Errors404 } from '@pages';

export interface ErrorProps {
  /** Suspense error */
  error: Error;
}

export interface PageProps extends RouteComponentProps {
  /** Page content */
  children: React.ReactNode;
}

export const TemplatePage: React.FC<PageProps> = ({ children }: PageProps) => {
  const {
    state: { user }
  } = useContext(ApplicationContext);

  // Signed out, the app sends the visitor to /admin/login; show a loader until then.
  const isAdmin = user?.role.name === 'Admin' || user?.role.name === 'Super Admin';
  const isFetching = useIsFetching();
  const { pathname } = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    setMobileNavOpen(false);
  }, [pathname]);

  return (
    <StyledAdmin className="App">
      <Paper className="sidebar" square elevation={0}>
        <SideNav
          isSuperAdmin={false}
          mobileOpen={mobileNavOpen}
          onMobileClose={() => setMobileNavOpen(false)}
        />
      </Paper>
      <div className="container">
        {isFetching > 0 && (
          <LinearProgress
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1301,
              height: 3
            }}
          />
        )}
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            alignItems: 'center',
            px: 1,
            py: 0.5,
            backgroundColor: '#fff',
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)'
          }}
        >
          <IconButton
            aria-label="Open navigation"
            onClick={() => setMobileNavOpen(true)}
            size="small"
          >
            <MenuIcon />
          </IconButton>
        </Box>
        <Paper className="main" square elevation={0}>
          <Box component="main" height="100%" display="flex" flexDirection="column">
            <Suspense fallback={<LoadingCircle size={150} />}>
              {!user ? (
                <LoadingCircle size={150} />
              ) : isAdmin ? (
                React.isValidElement(children) && React.cloneElement(children)
              ) : (
                <Errors404 variant="accessDenied" />
              )}
            </Suspense>
          </Box>
        </Paper>
      </div>
    </StyledAdmin>
  );
};

export default TemplatePage;
